/*
  Cactus Corn gift boxes: the recipient list engine.
  Reads CSV files, tab-separated text (what you get when you copy cells out of Excel or Google Sheets),
  and Excel .xlsx files. It finds the header row, matches columns to our fields, tidies every row (split
  full names, fix ZIP codes Excel stripped, turn state names into codes), and checks each address.
  Everything runs in the buyer's browser; nothing here talks to a server.
  Used by cc-gift-box-builder.js through window.CCGBRecipients.
*/
(() => {
  'use strict';

  /* ---------- Fields ---------- */
  const FIELDS = [
    { key: 'first', label: 'First name' },
    { key: 'last', label: 'Last name' },
    { key: 'full', label: 'Full name (we split it)' },
    { key: 'company', label: 'Company' },
    { key: 'address1', label: 'Address line 1' },
    { key: 'address2', label: 'Address line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zip', label: 'ZIP code' },
    { key: 'csz', label: 'City, state, and ZIP together' },
    { key: 'country', label: 'Country' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'message', label: 'Gift message' },
    { key: 'from', label: 'Signed from' }
  ];
  const FIELD_KEYS = FIELDS.map(f => f.key);

  // Header names we recognize, after normHeader(). "Notes" is left out on purpose: a notes column is
  // often internal ("VIP", "don't send to Bob") and must never end up printed on a gift card.
  const SYNONYMS = {
    first: ['first name', 'first', 'firstname', 'fname', 'given name', 'given', 'forename', 'name first', 'first nm'],
    last: ['last name', 'last', 'lastname', 'lname', 'surname', 'family name', 'name last', 'last nm'],
    full: ['name', 'full name', 'fullname', 'recipient', 'recipient name', 'contact', 'contact name', 'attention', 'attn', 'ship to', 'addressee', 'to', 'person', 'employee', 'employee name', 'customer name', 'client name', 'guest name'],
    company: ['company', 'company name', 'organization', 'organisation', 'org', 'business', 'business name', 'employer', 'firm', 'account', 'account name', 'agency'],
    address1: ['address', 'address 1', 'address1', 'address line 1', 'address line1', 'street', 'street address', 'street 1', 'street1', 'addr', 'addr1', 'addr 1', 'street address 1', 'street address line 1', 'line 1', 'address one'],
    address2: ['address 2', 'address2', 'address line 2', 'address line2', 'street 2', 'street2', 'addr2', 'addr 2', 'street address 2', 'street address line 2', 'line 2', 'apt', 'apt suite', 'apartment', 'suite', 'unit', 'apt suite unit', 'suite apt', 'apt unit', 'floor', 'building', 'bldg', 'apt no', 'suite no', 'address two'],
    city: ['city', 'town', 'city town', 'municipality', 'locality'],
    state: ['state', 'st', 'province', 'state province', 'region', 'state code', 'state abbreviation', 'state abbr', 'prov', 'state region'],
    zip: ['zip', 'zip code', 'zipcode', 'zip 4', 'zip4', 'postal code', 'postcode', 'postal', 'post code', 'zip postal code', 'zip postal', 'postal zip', 'zip or postal code'],
    csz: ['city state zip', 'city state zipcode', 'city state zip code', 'csz', 'city st zip', 'city state postal code'],
    country: ['country', 'country code', 'nation', 'country region'],
    email: ['email', 'e mail', 'email address', 'e mail address', 'mail', 'emails', 'email addr'],
    phone: ['phone', 'phone number', 'telephone', 'telephone number', 'tel', 'mobile', 'mobile phone', 'cell', 'cell phone', 'mobile number', 'phone no', 'contact number', 'phone num'],
    message: ['gift message', 'message', 'card message', 'greeting', 'personal message', 'custom note', 'card note', 'gift note', 'custom message', 'sentiment', 'card text', 'message text', 'gift card message', 'note to recipient'],
    from: ['from', 'sender', 'signed', 'signature', 'sign off', 'signoff', 'signed from', 'from name', 'sender name', 'closing', 'signed by']
  };
  const SYN_INDEX = new Map();
  for (const [key, list] of Object.entries(SYNONYMS)) for (const s of list) if (!SYN_INDEX.has(s)) SYN_INDEX.set(s, key);

  const PREFIXES = /^(recipient|recipients|ship to|shipping|mailing|contact|home|delivery|deliver to|send to|gift recipient|recipient s)\s+/;
  function normHeader(s) {
    let t = String(s == null ? '' : s).toLowerCase()
      .replace(/\(.*?\)|\[.*?\]/g, ' ')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const stripped = t.replace(PREFIXES, '');
    if (stripped && stripped !== t && (SYN_INDEX.has(stripped) || !SYN_INDEX.has(t))) t = stripped;
    return t;
  }

  function guessField(header) {
    const t = normHeader(header);
    if (!t) return null;
    if (SYN_INDEX.has(t)) return { key: SYN_INDEX.get(t), exact: true };
    const has = w => new RegExp('\\b' + w + '\\b').test(t);
    let key = null;
    if (has('email') || has('e mail')) key = 'email';
    else if (has('phone') || has('mobile') || has('cell') || has('telephone')) key = 'phone';
    else if (has('city') && (has('state') || has('zip'))) key = 'csz';
    else if (has('zip') || has('postal') || has('zipcode')) key = 'zip';
    else if (has('company') || has('organization') || has('organisation') || has('business')) key = 'company';
    else if (has('first') && has('name')) key = 'first';
    else if ((has('last') || has('sur')) && has('name')) key = 'last';
    else if ((has('address') || has('street')) && (/\b(2|two|line 2|apt|suite|unit)\b/.test(t))) key = 'address2';
    else if (has('address') || has('street')) key = 'address1';
    else if (has('city')) key = 'city';
    else if (has('state') || has('province')) key = 'state';
    else if (has('country')) key = 'country';
    else if (has('message') || has('greeting')) key = 'message';
    return key ? { key, exact: false } : null;
  }

  /* ---------- States and ZIP codes ---------- */
  const STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
    DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
    VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    PR: 'Puerto Rico', VI: 'U.S. Virgin Islands', GU: 'Guam', AS: 'American Samoa', MP: 'Northern Mariana Islands',
    FM: 'Micronesia', MH: 'Marshall Islands', PW: 'Palau',
    AA: 'Armed Forces Americas', AE: 'Armed Forces Europe', AP: 'Armed Forces Pacific'
  };
  const FAR = new Set(['AK', 'HI']);
  const FOREIGN_CITY = /^(paris|london|toronto|vancouver|montreal|montr\u00e9al|calgary|ottawa|mexico city|ciudad de m\u00e9xico|guadalajara|monterrey|tijuana|sydney|melbourne|auckland|tokyo|osaka|seoul|beijing|shanghai|hong kong|singapore|mumbai|delhi|new delhi|bangalore|bengaluru|dubai|tel aviv|berlin|munich|m\u00fcnchen|hamburg|frankfurt|amsterdam|rotterdam|brussels|bruxelles|zurich|z\u00fcrich|geneva|gen\u00e8ve|vienna|wien|madrid|barcelona|lisbon|lisboa|rome|roma|milan|milano|dublin|edinburgh|glasgow|manchester|stockholm|oslo|copenhagen|k\u00f8benhavn|helsinki|warsaw|prague|praha|budapest|athens|istanbul|cape town|johannesburg|lagos|nairobi|cairo|s\u00e3o paulo|sao paulo|rio de janeiro|buenos aires|santiago|lima|bogot\u00e1|bogota)$/i;
  const FOREIGN_STREET = /\b(rue|calle|carrera|avenida|via|viale|piazza|chemin|boulevard de|straat|gracht|ulica|rua)\b|(strasse|stra\u00dfe|gasse|platz)\b/i;
  const CANADA = /^(AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT|ALBERTA|BRITISH COLUMBIA|MANITOBA|NEW BRUNSWICK|NEWFOUNDLAND|NOVA SCOTIA|ONTARIO|QUEBEC|SASKATCHEWAN|YUKON)$/i;
  const TERRITORY = new Set(['PR', 'VI', 'GU', 'AS', 'MP', 'FM', 'MH', 'PW']);
  const MILITARY = new Set(['AA', 'AE', 'AP']);
  const STATE_ALIASES = {
    'washington dc': 'DC', 'washington d c': 'DC', 'dist of columbia': 'DC', 'district of columbia': 'DC', 'd c': 'DC',
    'us virgin islands': 'VI', 'u s virgin islands': 'VI', 'virgin islands': 'VI', 'usvi': 'VI',
    'n mariana islands': 'MP', 'northern marianas': 'MP', 'cnmi': 'MP', 'federated states of micronesia': 'FM',
    ala: 'AL', ariz: 'AZ', ark: 'AR', calif: 'CA', cal: 'CA', cali: 'CA', colo: 'CO', conn: 'CT', del: 'DE', fla: 'FL',
    ill: 'IL', ind: 'IN', kan: 'KS', kans: 'KS', mass: 'MA', mich: 'MI', minn: 'MN', miss: 'MS', mont: 'MT',
    neb: 'NE', nebr: 'NE', nev: 'NV', okla: 'OK', ore: 'OR', oreg: 'OR', penn: 'PA', penna: 'PA', tenn: 'TN',
    tex: 'TX', wash: 'WA', 'w va': 'WV', wva: 'WV', wis: 'WI', wisc: 'WI', wyo: 'WY'
  };
  const STATE_LOOKUP = new Map();
  for (const [code, name] of Object.entries(STATE_NAMES)) {
    STATE_LOOKUP.set(code.toLowerCase(), code);
    STATE_LOOKUP.set(name.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' '), code);
  }
  for (const [alias, code] of Object.entries(STATE_ALIASES)) STATE_LOOKUP.set(alias, code);

  function normState(s) {
    const t = String(s || '').toLowerCase().replace(/\./g, '').replace(/[^a-z ]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    return STATE_LOOKUP.get(t) || STATE_LOOKUP.get(t.replace(/^state of /, '')) || null;
  }

  // First three digits of a ZIP code -> state. Good enough to catch most typos; a mismatch is only a warning.
  const ZIP3 = [
    [5, 5, 'NY'], [6, 7, 'PR'], [8, 8, 'VI'], [9, 9, 'PR'], [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'],
    [50, 54, 'VT'], [55, 55, 'MA'], [56, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'], [90, 98, 'AE'],
    [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 200, 'DC'], [201, 201, 'VA'], [202, 205, 'DC'],
    [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'],
    [320, 339, 'FL'], [340, 340, 'AA'], [341, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'],
    [398, 399, 'GA'], [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'], [500, 528, 'IA'],
    [530, 549, 'WI'], [550, 567, 'MN'], [569, 569, 'DC'], [570, 577, 'SD'], [580, 588, 'ND'], [590, 599, 'MT'],
    [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'], [700, 714, 'LA'], [716, 729, 'AR'],
    [730, 731, 'OK'], [733, 733, 'TX'], [734, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'], [820, 831, 'WY'],
    [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'], [885, 885, 'TX'], [889, 898, 'NV'],
    [900, 961, 'CA'], [962, 966, 'AP'], [967, 968, 'HI'], [969, 969, 'GU'], [970, 979, 'OR'], [980, 994, 'WA'],
    [995, 999, 'AK']
  ];
  function zipState(zip) {
    const z5 = String(zip || '').slice(0, 5);
    if (!/^\d{5}$/.test(z5)) return null;
    if (z5 === '96799') return ['AS'];
    const p = parseInt(z5.slice(0, 3), 10);
    for (const [lo, hi, st] of ZIP3) if (p >= lo && p <= hi) return st === 'GU' ? ['GU', 'MP', 'FM', 'MH', 'PW'] : [st];
    return null;
  }

  // Excel drops leading zeros (02134 becomes 2134) and sometimes adds ".0".
  function normZip(raw) {
    let s = String(raw == null ? '' : raw).trim();
    if (!s) return { zip: '', fix: null, ok: false };
    s = s.replace(/\.0+$/, '');
    const digits = s.replace(/\D/g, '');
    let zip = null, fix = null;
    if (digits.length >= 1 && digits.length <= 4 && /^\d+$/.test(s)) { zip = digits.padStart(5, '0'); fix = `ZIP ${s} was missing its leading zero`; }
    else if (digits.length === 5) zip = digits;
    else if (digits.length === 8) { zip = digits.padStart(9, '0'); zip = zip.slice(0, 5) + '-' + zip.slice(5); fix = `ZIP ${s} was missing its leading zero`; }
    else if (digits.length === 9) zip = digits.slice(0, 5) + '-' + digits.slice(5);
    return zip ? { zip, fix, ok: true } : { zip: s, fix: null, ok: false };
  }

  function normCountry(raw) {
    const t = String(raw || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
    if (!t || ['us', 'usa', 'u s', 'u s a', 'united states', 'united states of america', 'america', 'unites states', 'united state'].includes(t)) return 'US';
    if (['pr', 'puerto rico', 'guam', 'gu', 'vi', 'us virgin islands', 'virgin islands', 'american samoa', 'as'].includes(t)) return 'US';
    return null;
  }

  /* ---------- Names ---------- */
  const SUFFIX = /^(jr|sr|ii|iii|iv|v|phd|md|dds|esq|cpa|mba|rn)\.?$/i;
  const PARTICLE = /^(van|von|de|da|del|della|di|le|la|du|dos|das|st\.?|bin|al|ter|ten)$/i;
  const HONORIFIC = /^(mr|mrs|ms|miss|mx|dr|prof|rev|hon)\.?\s+/i;
  function splitName(raw) {
    let s = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!s) return { first: '', last: '', note: null };
    let note = null;
    if (s.includes(',')) {
      const i = s.indexOf(',');
      const a = s.slice(0, i).trim(), b = s.slice(i + 1).trim();
      if (SUFFIX.test(b)) { const p = splitName(a); return { first: p.first, last: (p.last + ', ' + b).replace(/^, /, ''), note: p.note }; }
      if (a && b) { note = `Read "${s}" as ${b} ${a}`; s = b + ' ' + a; } // "Smith, Jane" -> "Jane Smith"
    }
    if (HONORIFIC.test(s) && s.replace(HONORIFIC, '').trim().includes(' ')) { note = (note ? note + '. ' : '') + `Took "${s.match(HONORIFIC)[0].trim()}" off the first name`; s = s.replace(HONORIFIC, '').trim(); }
    const parts = s.split(' ');
    if (parts.length === 1) return { first: parts[0], last: '', note };
    let last = parts.pop();
    if (SUFFIX.test(last) && parts.length > 1) last = parts.pop() + ' ' + last;
    while (parts.length > 1 && PARTICLE.test(parts[parts.length - 1])) last = parts.pop() + ' ' + last;
    return { first: parts.join(' '), last, note };
  }

  /* ---------- Reading files ---------- */
  function userError(text) { const e = new Error(text); e.user = true; return e; }

  function decode(buf) {
    const b = new Uint8Array(buf);
    if (b[0] === 0xFF && b[1] === 0xFE) return new TextDecoder('utf-16le').decode(b);
    if (b[0] === 0xFE && b[1] === 0xFF) return new TextDecoder('utf-16be').decode(b);
    let text = new TextDecoder('utf-8').decode(b);
    if (text.includes('\uFFFD')) { try { text = new TextDecoder('windows-1252').decode(b); } catch (e) { /* keep utf-8 */ } }
    return text.replace(/^\uFEFF/, '');
  }

  function sniffDelimiter(text) {
    const lines = [];
    let cur = '', q = false;
    for (let i = 0; i < text.length && lines.length < 12; i++) {
      const c = text[i];
      if (c === '"') q = !q;
      if (!q && (c === '\n' || c === '\r')) { if (cur.trim()) lines.push(cur); cur = ''; continue; }
      cur += c;
    }
    if (cur.trim() && lines.length < 12) lines.push(cur);
    let best = ',', bestScore = -1;
    for (const d of ['\t', ',', ';', '|']) {
      const counts = lines.map(l => { let n = 0, inq = false; for (const c of l) { if (c === '"') inq = !inq; else if (!inq && c === d) n++; } return n; });
      const nonzero = counts.filter(n => n > 0);
      if (!nonzero.length) continue;
      const mode = nonzero.sort((a, b) => nonzero.filter(v => v === b).length - nonzero.filter(v => v === a).length)[0];
      const score = counts.filter(n => n === mode).length * 10 + mode;
      if (score > bestScore) { bestScore = score; best = d; }
    }
    return best;
  }

  function parseDelimited(text, delim) {
    const rows = [];
    let row = [], field = '', q = false, i = 0;
    const n = text.length;
    while (i < n) {
      const c = text[i];
      if (q) {
        if (c === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } q = false; i++; continue; }
        field += c; i++; continue;
      }
      if (c === '"' && field.trim() === '') { field = ''; q = true; i++; continue; }
      if (c === delim) { row.push(field); field = ''; i++; continue; }
      if (c === '\r' || c === '\n') {
        row.push(field); rows.push(row); row = []; field = '';
        if (c === '\r' && text[i + 1] === '\n') i++;
        i++; continue;
      }
      field += c; i++;
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  function readText(text) {
    const clean = String(text || '').replace(/^\uFEFF/, '');
    const delim = sniffDelimiter(clean);
    return [{ name: 'Sheet 1', rows: parseDelimited(clean, delim) }];
  }

  async function inflateRaw(u8) {
    const stream = new Blob([u8]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  function unzip(buf) {
    const u8 = new Uint8Array(buf), dv = new DataView(buf);
    let eocd = -1;
    for (let i = u8.length - 22; i >= Math.max(0, u8.length - 65557); i--) if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    if (eocd < 0) throw userError('That file isn\'t a readable Excel file. Save it as CSV, or copy and paste the cells.');
    const count = dv.getUint16(eocd + 10, true);
    let p = dv.getUint32(eocd + 16, true);
    const entries = new Map();
    const utf8 = new TextDecoder('utf-8');
    for (let k = 0; k < count && p + 46 <= u8.length; k++) {
      if (dv.getUint32(p, true) !== 0x02014b50) break;
      const method = dv.getUint16(p + 10, true);
      const csize = dv.getUint32(p + 20, true);
      const nlen = dv.getUint16(p + 28, true), xlen = dv.getUint16(p + 30, true), clen = dv.getUint16(p + 32, true);
      const off = dv.getUint32(p + 42, true);
      const name = utf8.decode(u8.subarray(p + 46, p + 46 + nlen)).replace(/\\/g, '/');
      entries.set(name.toLowerCase(), { method, csize, off });
      p += 46 + nlen + xlen + clen;
    }
    return {
      async text(name) {
        const e = entries.get(String(name).toLowerCase());
        if (!e) return null;
        const start = e.off + 30 + dv.getUint16(e.off + 26, true) + dv.getUint16(e.off + 28, true);
        const data = u8.subarray(start, start + e.csize);
        const out = e.method === 0 ? data : e.method === 8 ? await inflateRaw(data) : null;
        if (!out) throw userError('That Excel file uses a format we can\'t read. Save it as CSV instead.');
        return utf8.decode(out);
      }
    };
  }

  const kids = (el, name) => Array.from(el.children).filter(c => c.localName === name);
  const all = (root, name) => Array.from(root.getElementsByTagNameNS('*', name));
  function colIndex(ref) {
    const m = /^([A-Z]+)/i.exec(ref || '');
    if (!m) return -1;
    let n = 0;
    for (const ch of m[1].toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1;
  }
  function siText(si) {
    // Rich text has several runs; phonetic guides (rPh) are left out.
    let s = '';
    for (const t of all(si, 't')) { let p = t.parentNode, skip = false; while (p && p !== si) { if (p.localName === 'rPh') { skip = true; break; } p = p.parentNode; } if (!skip) s += t.textContent; }
    return s;
  }
  function formatNumber(v, code) {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    const c = String(code || '').replace(/\\/g, '').replace(/"/g, '');
    if (/^0+$/.test(c) && Number.isInteger(n)) return String(Math.round(n)).padStart(c.length, '0');
    if (/^0{5}-0{4}$/.test(c) && Number.isInteger(n)) { const d = String(Math.round(n)).padStart(9, '0'); return d.slice(0, 5) + '-' + d.slice(5); }
    if (Number.isInteger(n)) return n.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 0 });
    return String(+n.toPrecision(12));
  }

  async function readXlsx(buf) {
    if (typeof DecompressionStream === 'undefined') throw userError('This browser can\'t open Excel files. Save the sheet as CSV, or copy and paste the cells.');
    const zip = unzip(buf);
    const parse = async name => { const t = await zip.text(name); return t ? new DOMParser().parseFromString(t, 'application/xml') : null; };
    const wb = await parse('xl/workbook.xml');
    if (!wb) throw userError('That doesn\'t look like an Excel workbook. Save it as CSV, or copy and paste the cells.');
    const rels = await parse('xl/_rels/workbook.xml.rels');
    const relMap = {};
    if (rels) for (const r of all(rels, 'Relationship')) relMap[r.getAttribute('Id')] = r.getAttribute('Target');
    const shared = [];
    const ss = await parse('xl/sharedStrings.xml');
    if (ss) for (const si of all(ss, 'si')) shared.push(siText(si));
    const fmtCodes = {}, xfFmt = [];
    const st = await parse('xl/styles.xml');
    if (st) {
      for (const f of all(st, 'numFmt')) fmtCodes[f.getAttribute('numFmtId')] = f.getAttribute('formatCode');
      const xfs = all(st, 'cellXfs')[0];
      if (xfs) for (const xf of kids(xfs, 'xf')) xfFmt.push(xf.getAttribute('numFmtId'));
    }
    const sheets = [];
    for (const s of all(wb, 'sheet')) {
      const rid = s.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') || s.getAttribute('r:id');
      let target = relMap[rid];
      if (!target) continue;
      target = target.replace(/^\/+/, '');
      if (!/^xl\//i.test(target)) target = 'xl/' + target;
      const doc = await parse(target);
      if (!doc) continue;
      const rows = [];
      for (const row of all(doc, 'row')) {
        const r = (parseInt(row.getAttribute('r'), 10) || rows.length + 1) - 1;
        const cells = [];
        for (const c of kids(row, 'c')) {
          let col = colIndex(c.getAttribute('r'));
          if (col < 0) col = cells.length;
          const t = c.getAttribute('t');
          const vEl = kids(c, 'v')[0];
          const v = vEl ? vEl.textContent : '';
          let val = '';
          if (t === 's') val = shared[parseInt(v, 10)] || '';
          else if (t === 'inlineStr') { const is = kids(c, 'is')[0]; val = is ? siText(is) : ''; }
          else if (t === 'b') val = v === '1' ? 'TRUE' : 'FALSE';
          else if (t === 'e') val = '';
          else if (t === 'str' || t === 'd') val = v;
          else if (v !== '') val = formatNumber(v, fmtCodes[xfFmt[parseInt(c.getAttribute('s'), 10) || 0]]);
          cells[col] = val;
        }
        rows[r] = Array.from(cells, x => x == null ? '' : x);
      }
      sheets.push({ name: s.getAttribute('name') || 'Sheet', rows: Array.from(rows, x => x || []) });
    }
    if (!sheets.length) throw userError('We couldn\'t find any sheets in that file.');
    return sheets;
  }

  async function readFile(file) {
    const name = file.name || 'list';
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (['xls', 'numbers', 'ods'].includes(ext)) {
      const app = ext === 'numbers' ? 'Numbers' : ext === 'ods' ? 'your spreadsheet app' : 'Excel';
      throw userError(`We can't open .${ext} files. In ${app}, save the list as CSV or as an Excel workbook (.xlsx), or copy and paste the cells.`);
    }
    const buf = await file.arrayBuffer();
    const head = new Uint8Array(buf.slice(0, 4));
    const isZip = head[0] === 0x50 && head[1] === 0x4B;
    if (ext === 'xlsx' || ext === 'xlsm' || isZip) return { kind: 'xlsx', fileName: name, sheets: await readXlsx(buf) };
    if (head[0] === 0xD0 && head[1] === 0xCF) throw userError('That looks like an older Excel file (.xls). Save it as CSV or .xlsx and try again.');
    return { kind: 'csv', fileName: name, sheets: readText(decode(buf)) };
  }

  /* ---------- Finding the header and matching columns ---------- */
  const isBlankRow = r => !r || r.every(c => String(c == null ? '' : c).trim() === '');

  // Finds the header row, or uses forcedHeader (a row index, or -1 for "my list has no header row").
  function detect(rows, forcedHeader) {
    const forced = Number.isInteger(forcedHeader);
    const max = forced ? 0 : Math.min(rows.length, 15);
    let headerIndex = forced ? forcedHeader : -1, best = 1;
    for (let i = 0; i < max; i++) {
      const r = rows[i] || [];
      const keys = new Set();
      let exact = 0;
      for (const c of r) { const g = guessField(c); if (g) { keys.add(g.key); if (g.exact) exact++; } }
      const score = keys.size + exact * 0.1;
      if (keys.size >= 2 && score > best) { best = score; headerIndex = i; }
    }
    const width = Math.max(0, ...rows.slice(0, 200).map(r => (r || []).length));
    const header = headerIndex >= 0 ? rows[headerIndex] : [];
    const mapping = new Array(width).fill(null);
    const used = new Set();
    // Exact matches win, then the looser guesses, left to right.
    for (const pass of [true, false]) {
      for (let c = 0; c < width; c++) {
        if (mapping[c]) continue;
        const g = guessField(header[c]);
        if (!g || g.exact !== pass || used.has(g.key)) continue;
        mapping[c] = g.key; used.add(g.key);
      }
    }
    // A sheet without a header: guess from what's in each column.
    if (headerIndex < 0) guessFromContent(rows, mapping, used);
    // Full name only counts when there is no first-name column.
    if (used.has('first') && used.has('full')) { const i = mapping.indexOf('full'); if (i >= 0 && used.has('last')) mapping[i] = null; }
    const columns = [];
    const start = headerIndex + 1;
    for (let c = 0; c < width; c++) {
      const samples = [];
      for (let r = start; r < rows.length && samples.length < 3; r++) { const v = String((rows[r] || [])[c] == null ? '' : rows[r][c]).trim(); if (v) samples.push(v); }
      columns.push({ index: c, header: String(header[c] == null ? '' : header[c]).trim(), samples });
    }
    const mapped = new Set(mapping.filter(Boolean));
    const complete = (mapped.has('first') || mapped.has('full')) && mapped.has('address1') && ((mapped.has('city') && mapped.has('state') && mapped.has('zip')) || mapped.has('csz'));
    return { headerIndex, mapping, columns, confidence: headerIndex >= 0 && complete ? 'high' : 'low' };
  }

  function guessFromContent(rows, mapping, used) {
    const width = mapping.length;
    const take = (c, key) => { if (!used.has(key) && !mapping[c]) { mapping[c] = key; used.add(key); } };
    for (let c = 0; c < width; c++) {
      const vals = rows.slice(0, 60).map(r => String((r || [])[c] == null ? '' : r[c]).trim()).filter(Boolean);
      if (!vals.length) continue;
      const share = re => vals.filter(v => re.test(v)).length / vals.length;
      if (share(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/) > 0.7) take(c, 'email');
      else if (share(/^\d{3,5}(-\d{4})?$/) > 0.7) take(c, 'zip');
      else if (vals.filter(v => normState(v)).length / vals.length > 0.7 && vals.every(v => v.length <= 20)) take(c, 'state');
      else if (share(/^[+(]?\d[\d\s().-]{8,}$/) > 0.7) take(c, 'phone');
      else if (share(/^\d+[a-z]?\s+\S+/i) > 0.6) take(c, 'address1');
    }
    // The column just left of the state is usually the city, and the first column of words is usually the name.
    const words = c => { const vals = rows.slice(0, 60).map(r => String((r || [])[c] == null ? '' : r[c]).trim()).filter(Boolean); return vals.length && vals.filter(v => /^[A-Za-z\u00C0-\u017F.' -]+$/.test(v)).length / vals.length > 0.7; };
    const st = mapping.indexOf('state');
    if (st > 0 && !mapping[st - 1] && words(st - 1)) take(st - 1, 'city');
    if (!used.has('first') && !used.has('full')) for (let c = 0; c < width; c++) if (!mapping[c] && words(c)) { take(c, 'full'); break; }
  }

  /* ---------- Building and checking rows ---------- */
  const clean = s => String(s == null ? '' : s).replace(/[\u00A0\u2007\u202F]/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, ' ').trim();
  const cleanMessage = s => String(s == null ? '' : s).replace(/\r\n?/g, '\n').replace(/[\u00A0\u2007\u202F]/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').split('\n').map(l => l.replace(/[ \t]+/g, ' ').trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const CSZ = /^\s*(.+?)[,\s]+([A-Za-z][A-Za-z. ]{0,24}?)[,\s]+(\d{3,5}(?:-?\d{4})?)\s*$/;
  const FULL_ADDR = /^(.+?),\s*([^,]+?),\s*([A-Za-z][A-Za-z. ]{1,24}?)\s+(\d{3,5}(?:-?\d{4})?)\s*(?:,?\s*(?:usa|us|united states))?\s*$/i;
  let rowSeq = 0;

  function isExampleRow(r) {
    return /^jordan$/i.test(r.first || '') && /^example$/i.test(r.last || '') && /^123 example st/i.test(r.address1 || '');
  }

  function normalize(raw) {
    const row = { id: 'r' + (++rowSeq), src: raw.src || 0, first: '', last: '', company: '', address1: '', address2: '', city: '', state: '', zip: '', country: 'US', email: '', phone: '', message: '', from: '', fixes: [], issues: [], status: 'ok', badCountry: '' };
    const g = k => clean(raw[k]);
    row.first = g('first'); row.last = g('last'); row.company = g('company');
    row.address1 = g('address1'); row.address2 = g('address2');
    row.city = g('city'); row.email = g('email').toLowerCase(); row.phone = g('phone');
    row.message = cleanMessage(raw.message); row.from = g('from');

    if (!row.first && !row.last && g('full')) {
      const p = splitName(g('full'));
      row.first = p.first; row.last = p.last;
      row.splitName = true;
      if (p.note) row.fixes.push(p.note);
    } else if (row.first && !row.last && raw.__hasLast === false && row.first.includes(' ')) {
      const p = splitName(row.first);
      if (p.last) { row.first = p.first; row.last = p.last; row.splitName = true; }
    }
    if (HONORIFIC.test(row.first) && row.first.replace(HONORIFIC, '').trim()) { row.fixes.push(`Took "${row.first.match(HONORIFIC)[0].trim()}" off the first name`); row.first = row.first.replace(HONORIFIC, '').trim(); }

    let state = g('state'), zip = g('zip');
    const csz = g('csz');
    if (csz && (!row.city || !state || !zip)) {
      const m = CSZ.exec(csz);
      if (m && normState(m[2])) { row.city = row.city || m[1].replace(/,$/, '').trim(); state = state || m[2]; zip = zip || m[3]; row.fixes.push('Split city, state, and ZIP into their own columns'); }
      else if (!row.city) row.city = csz;
    }
    if (!row.city && !state && !zip && row.address1) {
      const m = FULL_ADDR.exec(row.address1);
      if (m && normState(m[3])) { row.address1 = m[1].trim(); row.city = m[2].trim(); state = m[3]; zip = m[4]; row.fixes.push('Split the full address into street, city, state, and ZIP'); }
    }
    // A ZIP code typed into the state column ("AZ 85004")
    if (state && !zip) { const m = /^([A-Za-z. ]+?)\s+(\d{5}(?:-\d{4})?)$/.exec(state); if (m && normState(m[1])) { state = m[1]; zip = m[2]; row.fixes.push('Moved the ZIP code out of the state column'); } }

    const z = normZip(zip);
    row.zip = z.zip; if (z.fix) row.fixes.push(z.fix);
    row.zipOk = z.ok;
    const code = normState(state);
    if (code) { row.state = code; if (state.trim().toUpperCase() !== code) row.fixes.push(`Changed "${state.trim()}" to ${code}`); }
    else if (!state && z.ok) {
      const guess = zipState(row.zip);
      if (guess && guess.length === 1) { row.state = guess[0]; row.stateGuessed = true; }
    } else row.state = state;
    row.stateOk = !!STATE_NAMES[row.state];

    const country = g('country');
    if (normCountry(country) === 'US') row.country = 'US';
    else { row.country = country; row.badCountry = country; }

    if (row.phone) {
      if (/e\+?\d/i.test(row.phone)) row.phoneBroken = true;
      else {
        let d = row.phone.replace(/\D/g, '');
        if (d.length === 11 && d[0] === '1') d = d.slice(1);
        if (d.length === 10) row.phone = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
      }
    }
    if (/^(apo|fpo|dpo)$/i.test(row.city)) row.city = row.city.toUpperCase();
    return row;
  }

  function issue(row, level, field, code, text) { row.issues.push({ level, field, code, text }); }

  function validateOne(row, opts = {}) {
    row.issues = [];
    if (!row.first) issue(row, 'error', 'first', 'first', 'Add a first name.');
    if (!row.last) issue(row, 'warn', 'last', 'last', 'No last name. Carriers deliver best to a full name.');
    if (!row.address1) issue(row, 'error', 'address1', 'address1', 'Add a street address.');
    else {
      if (!/\d/.test(row.address1) && !/^(one|two|three|four|five|six|seven|eight|nine|ten)\b/i.test(row.address1)) issue(row, 'warn', 'address1', 'nonum', 'No street number. Please check the address.');
      if (row.address1.length > 40) issue(row, 'warn', 'address1', 'long', 'Long address line. It may be shortened on the shipping label.');
    }
    if (row.address2 && row.address2.length > 40) issue(row, 'warn', 'address2', 'long', 'Long address line. It may be shortened on the shipping label.');
    if (!row.city) issue(row, 'error', 'city', 'city', 'Add a city.');
    if (!row.state) issue(row, 'error', 'state', 'state', 'Add a state, like AZ.');
    else if (!STATE_NAMES[row.state]) issue(row, 'error', 'state', 'state', `"${row.state}" isn't a US state. Use a 2-letter code, like AZ.`);
    if (!row.zip) issue(row, 'error', 'zip', 'zip', 'Add a ZIP code.');
    else if (!/^\d{5}(-\d{4})?$/.test(row.zip)) issue(row, 'error', 'zip', 'zip', `"${row.zip}" isn't a ZIP code. Use 5 digits, like 85004.`);
    else if (STATE_NAMES[row.state]) {
      const exp = zipState(row.zip);
      if (exp && !exp.includes(row.state) && !(MILITARY.has(row.state) && exp.some(s => MILITARY.has(s)))) issue(row, 'warn', 'zip', 'zipstate', `ZIP ${row.zip} is usually in ${exp[0]}, not ${row.state}. Please check both.`);
    }
    if (row.badCountry) issue(row, 'error', 'country', 'country', `We ship to US addresses only, and this one says "${row.badCountry}". Remove it, or email us about international gifts.`);
    else if (row.stateGuessed && (FOREIGN_CITY.test(row.city) || FOREIGN_STREET.test(row.address1))) {
      row.issues = row.issues.filter(i => i.field !== 'state' && i.field !== 'zip');
      issue(row, 'error', 'country', 'country', `This looks like an address outside the US (${row.city || row.address1}). We ship to US addresses only, so remove it, or email us about international gifts.`);
    } else if (row.stateGuessed) issue(row, 'warn', 'state', 'stateguess', `No state was given, so we used ${row.state} from ZIP ${row.zip}. Please check it.`);
    else if (CANADA.test(row.state) || /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(row.zip)) {
      row.issues = row.issues.filter(i => i.field !== 'state' && i.field !== 'zip');
      issue(row, 'error', 'country', 'country', 'This looks like a Canadian address. We ship to US addresses only, so remove it, or email us about international gifts.');
    }
    if (MILITARY.has(row.state) || /^(apo|fpo|dpo)$/i.test(row.city)) issue(row, 'info', 'state', 'military', 'Military address: needs a customs form and can take 2 to 4 weeks.');
    if (FAR.has(row.state) || TERRITORY.has(row.state) || MILITARY.has(row.state)) row.far = true; else row.far = false;
    if (row.far) issue(row, 'info', 'state', 'far', opts.farText || 'Ships outside the lower 48, so it has an added shipping charge.');
    if (row.email && !EMAIL.test(row.email)) issue(row, 'warn', 'email', 'email', 'This email doesn\'t look right, so it won\'t get tracking emails.');
    if (row.phoneBroken) issue(row, 'warn', 'phone', 'phone', 'Excel changed this phone number (it shows in scientific notation). Please retype it.');
    else if (row.phone && row.phone.replace(/\D/g, '').length !== 10) issue(row, 'warn', 'phone', 'phone', 'Phone numbers should have 10 digits.');
    if (row.company && row.company.length > 40) issue(row, 'warn', 'company', 'long', 'Long company name. It may be shortened on the shipping label.');
    // The builder checks that each card fits and warns about blank placeholders. It can return a
    // message (an error) or a list of { level, field, code, text }.
    if (opts.checkCard) for (const it of [].concat(opts.checkCard(row) || [])) issue(row, it.level || 'error', it.field || 'message', it.code || 'message', it.text || String(it));
    return row;
  }

  const dupKey = r => [r.first, r.last, r.address1, r.address2, String(r.zip).slice(0, 5)].join('|').toLowerCase().replace(/[^a-z0-9|]+/g, '');
  function markDuplicates(rows) {
    const seen = new Map();
    for (const r of rows) {
      r.issues = r.issues.filter(i => i.code !== 'dup');
      if (!r.first && !r.address1) continue;
      const k = dupKey(r);
      if (seen.has(k)) { const first = seen.get(k); r.dupOf = first.id; r.issues.push({ level: 'warn', field: 'first', code: 'dup', text: `Same person and address as row ${first.src || '?'}. Remove it unless they should get two boxes.` }); }
      else { seen.set(k, r); r.dupOf = null; }
    }
  }

  function finish(row) {
    row.status = row.issues.some(i => i.level === 'error') ? 'error' : row.issues.some(i => i.level === 'warn') ? 'warn' : 'ok';
    return row;
  }

  function validateAll(rows, opts) {
    for (const r of rows) validateOne(r, opts);
    markDuplicates(rows);
    for (const r of rows) finish(r);
    return rows;
  }

  function build(rows, headerIndex, mapping) {
    const out = [];
    const skipped = { empty: 0, example: 0 };
    const hasLast = mapping.includes('last');
    for (let i = headerIndex + 1; i < rows.length; i++) {
      const r = rows[i];
      if (isBlankRow(r)) { skipped.empty++; continue; }
      const raw = { src: i + 1, __hasLast: hasLast };
      mapping.forEach((key, c) => {
        if (!key) return;
        const v = r[c] == null ? '' : String(r[c]);
        raw[key] = raw[key] ? raw[key] + ' ' + v : v;
      });
      if (FIELD_KEYS.every(k => !String(raw[k] || '').trim())) { skipped.empty++; continue; }
      const row = normalize(raw);
      if (isExampleRow(row)) { skipped.example++; continue; }
      out.push(row);
    }
    return { rows: out, skipped };
  }

  function blankRow() { return normalize({}); }

  function summary(rows) {
    const s = { total: rows.length, ok: 0, warn: 0, error: 0, fixed: 0, far: 0, dup: 0, custom: 0, split: 0 };
    for (const r of rows) {
      s[r.status]++;
      if (r.splitName) s.split++;
      if (r.fixes.length) s.fixed++;
      if (r.far) s.far++;
      if (r.dupOf) s.dup++;
      if (r.message) s.custom++;
    }
    return s;
  }

  /* ---------- Writing CSV ---------- */
  const csvCell = v => { const s = String(v == null ? '' : v); return /[",\n\r]/.test(s) || /^\s|\s$/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const csvLine = cells => cells.map(csvCell).join(',');
  const TEMPLATE_HEADERS = ['First name (required)', 'Last name (required)', 'Company', 'Address line 1 (required)', 'Address line 2', 'City (required)', 'State (required)', 'ZIP code (required)', 'Email (for tracking)', 'Phone', 'Gift message (blank = your main message)', 'Signed from (blank = your sign-off)'];
  function templateCsv() {
    return '\uFEFF' + [TEMPLATE_HEADERS, ['Jordan', 'Example', 'Example Co.', '123 Example St', 'Suite 100', 'Phoenix', 'AZ', '85004', 'jordan@example.com', '602-555-0123', '', '']].map(csvLine).join('\r\n') + '\r\n';
  }
  const EXPORT_HEADERS = ['Row', 'First name', 'Last name', 'Company', 'Address line 1', 'Address line 2', 'City', 'State', 'ZIP code', 'Country', 'Email', 'Phone', 'Gift message', 'Signed from'];
  function toCsv(rows, opts = {}) {
    const lines = [EXPORT_HEADERS.concat(opts.extraHeaders || [])];
    rows.forEach((r, i) => lines.push([i + 1, r.first, r.last, r.company, r.address1, r.address2, r.city, r.state, r.zip, r.country, r.email, r.phone, r.message, r.from].concat(opts.extra ? opts.extra(r) : [])));
    return '\uFEFF' + lines.map(csvLine).join('\r\n') + '\r\n';
  }

  window.CCGBRecipients = {
    FIELDS, STATE_NAMES, FAR, TERRITORY, MILITARY,
    readFile, readText, readXlsx, parseDelimited, sniffDelimiter, decode,
    detect, build, normalize, validateOne, validateAll, markDuplicates, finish, summary, blankRow,
    splitName, normState, normZip, zipState, normHeader, guessField, isExampleRow,
    templateCsv, toCsv, csvLine
  };
})();
